/**
 * Client Portal — My Assessments page.
 * Shows completed assessments, plus any in-progress (saved) drafts.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Card, CardContent, LinearProgress,
  Grid,
} from '@mui/material';
import { Visibility, Send, PlayArrow, Timer, Delete, Feedback } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';
import PortalTooltip from '../../../components/PortalTooltip';
import FeedbackDialog from '../../../components/FeedbackDialog';

interface AssessmentItem {
  id: string;
  category: string;
  domain: string;
  domain_score: number | null;
  status: string;
  released_to_client: boolean;
  released_at: string | null;
  created_at: string | null;
  sent_to_sme: boolean;
}

interface DraftItem {
  id: string;
  category: string;
  domain: string;
  answered_count: number;
  total_questions: number;
  updated_at: string | null;
  expires_at: string | null;
}

const Assessments: React.FC = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState<AssessmentItem[]>([]);
  const [drafts, setDrafts] = useState<DraftItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewDialog, setViewDialog] = useState<AssessmentItem | null>(null);
  const [detailData, setDetailData] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<Record<string, 'idle' | 'submitting' | 'submitted'>>({});
  const [feedbackTarget, setFeedbackTarget] = useState<AssessmentItem | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [assessRes, draftRes] = await Promise.all([
        authFetch('/api/client/assessments'),
        authFetch('/api/client/assessment-drafts'),
      ]);
      const assessData = await assessRes.json();
      const draftData = await draftRes.json();
      if (assessData.success) setAssessments(assessData.assessments);
      if (draftData.success) setDrafts(draftData.drafts);
    } catch {
      setError('Failed to load assessments');
    } finally {
      setLastRefreshed(new Date());
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleView = async (assessment: AssessmentItem) => {
    setViewDialog(assessment);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const response = await authFetch(`/api/assessments/${assessment.id}`);
      const data = await response.json();
      if (data.success) setDetailData(data.result || data.assessment);
    } catch {
      // Detail load is best-effort
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSubmitToSME = async (assessmentId: string) => {
    setSubmitStatus((prev) => ({ ...prev, [assessmentId]: 'submitting' }));
    try {
      const response = await authFetch(`/api/client/assessments/${assessmentId}/submit-to-sme`, {
        method: 'POST',
      });
      if (response.ok) {
        setSubmitStatus((prev) => ({ ...prev, [assessmentId]: 'submitted' }));
        // Refetch so the sent_to_sme flag updates from backend
        fetchData();
      }
    } catch {
      setSubmitStatus((prev) => ({ ...prev, [assessmentId]: 'idle' }));
    }
  };

  const handleDelete = async (assessmentId: string) => {
    if (!window.confirm('Are you sure you want to delete this assessment? This cannot be undone.')) return;
    try {
      const response = await authFetch(`/api/client/assessments/${assessmentId}`, { method: 'DELETE' });
      if (response.ok) {
        setAssessments((prev) => prev.filter((a) => a.id !== assessmentId));
      }
    } catch { /* ignore */ }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <PortalTooltip tooltipKey="client.page.assessments" showIcon>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          My Assessments
        </Typography>
          <RefreshButton onRefresh={fetchData} loading={loading} lastRefreshed={lastRefreshed} />
        </Box>
      </PortalTooltip>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Completed assessments and in-progress drafts for your organization.
      </Typography>

      {/* ── In-Progress Drafts ── */}
      {drafts.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>In-Progress Assessments</Typography>
          <Grid container spacing={2}>
            {drafts.map((d) => {
              const progress = d.total_questions > 0 ? (d.answered_count / d.total_questions) * 100 : 0;
              const expiresDate = d.expires_at ? new Date(d.expires_at) : null;
              const daysLeft = expiresDate ? Math.max(0, Math.ceil((expiresDate.getTime() - Date.now()) / 86400000)) : null;
              return (
                <Grid item xs={12} md={6} lg={4} key={d.id}>
                  <Card sx={{ border: '2px solid #ff9800' }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{d.domain}</Typography>
                      <Typography variant="caption" color="text.secondary">{d.category}</Typography>
                      <Box sx={{ my: 1 }}>
                        <Typography variant="body2">{d.answered_count} / {d.total_questions} questions</Typography>
                        <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                        <Chip label="In Progress" size="small" color="warning" />
                        {daysLeft !== null && (
                          <Chip icon={<Timer sx={{ fontSize: 14 }} />} label={`${daysLeft}d left`} size="small"
                            color={daysLeft <= 1 ? 'error' : 'default'} />
                        )}
                      </Box>
                      <Button variant="contained" size="small" startIcon={<PlayArrow />}
                        onClick={() => navigate('/client/run-assessment')}
                        sx={{ bgcolor: '#00338D' }}>
                        Resume
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* ── Completed Assessments ── */}
      {assessments.length === 0 && drafts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No assessments have been completed yet. Run an assessment from the Assessment page.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Domain</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Workflow Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assessments.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{a.category}</TableCell>
                  <TableCell>{a.domain}</TableCell>
                  <TableCell>
                    {a.domain_score ? `${a.domain_score.toFixed(1)}/5.0` : '—'}
                  </TableCell>
                  <TableCell>
                    {a.released_to_client ? (
                      <Chip label="Report Released" size="small" color="success" />
                    ) : a.status === 'completed' ? (
                      <Chip label="Completed — Pending Review" size="small" color="info" />
                    ) : (
                      <Chip label={a.status} size="small" color="default" />
                    )}
                  </TableCell>
                  <TableCell>
                    {a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" startIcon={<Visibility />} variant="outlined" onClick={() => handleView(a)}>
                        View
                      </Button>
                      {a.status === 'completed' && !a.sent_to_sme && (submitStatus[a.id] || 'idle') !== 'submitted' && (
                        <Button
                          size="small"
                          startIcon={<Send />}
                          variant="contained"
                          sx={{ bgcolor: '#00338D' }}
                          disabled={(submitStatus[a.id] || 'idle') === 'submitting'}
                          onClick={() => handleSubmitToSME(a.id)}
                        >
                          {(submitStatus[a.id] || 'idle') === 'submitting' ? 'Sending...' : 'Send to SME'}
                        </Button>
                      )}
                      {(submitStatus[a.id] === 'submitted' || a.sent_to_sme) && (
                        <>
                          <Chip label="Sent to SME" size="small" color="success" />
                          <Button
                            size="small"
                            startIcon={<Send />}
                            variant="outlined"
                            color="info"
                            disabled={(submitStatus[a.id] || 'idle') === 'submitting'}
                            onClick={() => handleSubmitToSME(a.id)}
                          >
                            {(submitStatus[a.id] || 'idle') === 'submitting' ? 'Resending...' : 'Resend'}
                          </Button>
                        </>
                      )}
                      <Button
                        size="small"
                        startIcon={<Delete />}
                        variant="outlined"
                        color="error"
                        onClick={() => handleDelete(a.id)}
                      >
                        Delete
                      </Button>
                      {a.status === 'completed' && (
                        <Button
                          size="small"
                          startIcon={<Feedback />}
                          variant="outlined"
                          color="secondary"
                          onClick={() => setFeedbackTarget(a)}
                        >
                          Feedback
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Assessment Detail Dialog */}
      <Dialog open={!!viewDialog} onClose={() => setViewDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Assessment Details — {viewDialog?.domain}</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : detailData ? (
            <>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6">
                    Score: {(detailData.domainScore ?? detailData.domain_score)?.toFixed(1) || 'N/A'}/5.0
                  </Typography>
                  {(detailData.domainScore ?? detailData.domain_score) && (
                    <LinearProgress
                      variant="determinate"
                      value={((detailData.domainScore ?? detailData.domain_score) / 5) * 100}
                      sx={{ height: 8, borderRadius: 4, mt: 1 }}
                    />
                  )}
                </CardContent>
              </Card>
              {detailData.summary && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Summary</Typography>
                    <Typography variant="body2">{detailData.summary}</Typography>
                  </CardContent>
                </Card>
              )}
              {detailData.items && detailData.items.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Findings</Typography>
                    {detailData.items.map((item: any, idx: number) => (
                      <Box key={idx} sx={{ mb: 1.5, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="body2"><strong>Q{idx+1}:</strong> {item.question}</Typography>
                        <Typography variant="caption">Score: {item.score}/5 | Risk: {item.riskLevel}</Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Box sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Category: {viewDialog?.category}<br />
                Domain: {viewDialog?.domain}<br />
                Score: {viewDialog?.domain_score?.toFixed(1) || 'N/A'}/5.0<br />
                Status: {viewDialog?.status}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assessment Feedback Dialog */}
      <FeedbackDialog
        open={!!feedbackTarget}
        onClose={() => setFeedbackTarget(null)}
        assessmentId={feedbackTarget?.id || ''}
        assessmentDomain={feedbackTarget?.domain}
      />
    </Box>
  );
};

export default Assessments;
